import {
  Entity,
  EntityKey,
  EntityManager,
  EntityQuery,
  EntityType,
  FetchStrategy,
  FilterQueryOp,
  MetadataStore,
  Predicate,
  QueryResult,
} from 'breeze-client';
import _ from 'lodash-es';
import { BehaviorSubject, Observable, defer, from, of } from 'rxjs';
import { concatMap, filter, map, shareReplay, startWith } from 'rxjs/operators';
import { EntityValues, mapJsonObjectToClient } from '.';
import { isEntity } from '../utils/entity-helper';
import { EntityTypeLike, getEntityType } from './entity-selector';
import { mapValueToServer } from './map-json-server-object';
import { UnitOfWorkHelper } from './unit-of-work-helper';

export interface OrderByDescription {
  propertyPath: string;
  isDescending?: boolean;
}

export interface QueryBuilder<T extends Entity> {
  withParameters<TParams>(params: TParams): QueryBuilder<T>;
  where(predicate: Predicate): QueryBuilder<T>;
  where(...args: any[]): QueryBuilder<T>;
  orderBy(...args: OrderByDescription[]): QueryBuilder<T>;
  expand(...propertyPaths: EntityPropertyPaths<T>): QueryBuilder<T>;
}

export interface Page<T> {
  items: T[];
  totalNumberOfPages: number;
  pageIndex: number;
  itemCount: number;
  pageSize: number;
}

export interface Paginator<T> {
  page$: Observable<Page<T>>;
  setPageIndex(newPageIndex: number, newPageSize?: number): void;
}

export interface FetchOptions {
  // Refetch data upon clearing the unit of work. Ignored if executeLocally == true.
  refetchOnClear?: boolean;
  // Cache the last result and replay to new subscribers
  replayLastResult?: boolean;
  // Execute the query against the local Breeze entity cache
  executeLocally?: boolean;
}

export interface FetchParams<T extends Entity> {
  select: EntityPropertyPaths<T>;
  queryAction?: QueryBuilderAction<T>;
}

export interface FetchParamsToType<T extends Entity, TResult extends Entity> extends FetchParams<T> {
  toType: EntityTypeLike<TResult>;
}

export type EntityLike<T extends Entity> = EntityValues<T> & Record<string, any>;
export type EntityPropertyPaths<T extends Entity> = string[] | (keyof EntityValues<T>)[];

export type QueryBuilderAction<T extends Entity> = (queryBuilder: QueryBuilder<T>) => void;

export interface Repository<T extends Entity> {
  withId(key: any | any[], checkLocalCacheFirst?: boolean): Observable<T>;
  fetch(queryAction?: QueryBuilderAction<T>, options?: FetchOptions): Observable<T[]>;
  fetch<TResult>(queryAction?: QueryBuilderAction<T>, options?: FetchOptions): Observable<TResult[]>;
  fetch(args: FetchParams<T>, options?: FetchOptions): Observable<EntityLike<T>[]>;
  fetch<TResult extends Entity>(args: FetchParamsToType<T, TResult>, options?: FetchOptions): Observable<TResult[]>;
  fetch<TResult, TParams>(resourceName: string, params?: TParams): Observable<TResult[]>;
  fetchPage(initialPageSize: number, queryAction?: QueryBuilderAction<T>): Paginator<T>;
  fetchPage(initialPageSize: number, args: FetchParams<T>): Paginator<EntityLike<T>>;
  fetchPage<TResult extends Entity>(initialPageSize: number, args: FetchParamsToType<T, TResult>): Paginator<TResult>;
  count(queryAction?: QueryBuilderAction<T>): Observable<number>;
}

class EntityQueryBuilder<T extends Entity> implements QueryBuilder<T> {
  constructor(private _entityQuery: EntityQuery) {}

  get entityQuery(): EntityQuery {
    return this._entityQuery;
  }

  withParameters<TParams>(params: TParams): QueryBuilder<T> {
    if (params) {
      [params] = this.mapArgs(params);
      this._entityQuery = this._entityQuery.withParameters(params);
    }
    return this;
  }

  where(...args: any[]): QueryBuilder<T> {
    if (args && args.length) {
      args = this.mapArgs(...args);
      this._entityQuery = this._entityQuery.where(args);
    }
    return this;
  }

  orderBy(...args: OrderByDescription[]): QueryBuilder<T> {
    if (args && args.length) {
      this._entityQuery = this._entityQuery.orderBy(
        args.map((orderBy) => `${orderBy.propertyPath}${orderBy.isDescending ? ' desc' : ''}`)
      );
    }
    return this;
  }

  expand(...propertyPaths: EntityPropertyPaths<T>): QueryBuilder<T> {
    this._entityQuery = this._entityQuery.expand(propertyPaths as string[]);
    return this;
  }

  private mapArgs(...args: any[]): any[] {
    if (args && args.length) {
      if (args.length > 1) {
        return args.map((a) => {
          [a] = this.mapArgs(a);
          return a;
        });
      }

      let [arg] = args;
      if (arg instanceof Predicate) {
        arg = arg.toJSONExt(null);
      }

      if (arg instanceof Date) {
        arg = mapValueToServer(arg);
      } else if (Array.isArray(arg)) {
        arg = this.mapArgs(arg);
      } else if (typeof arg === 'object' && !(arg instanceof FilterQueryOp)) {
        arg = _.mapValues(arg, (value) => {
          if (value === null || value === undefined) {
            return value;
          }

          if (value instanceof Date) {
            return mapValueToServer(value);
          }

          if (Array.isArray(value)) {
            return this.mapArgs(...value);
          }

          if (typeof value === 'object') {
            [value] = this.mapArgs(value);
          }

          return value;
        });
      }

      return [arg];
    }

    return args;
  }
}

export class GenericRepository<T extends Entity> implements Repository<T> {
  protected readonly defaultFetchStrategy: FetchStrategy;

  constructor(
    protected readonly helper: UnitOfWorkHelper,
    private readonly manager: EntityManager,
    private readonly entityTypeLike: EntityTypeLike<T>,
    protected readonly expand: EntityPropertyPaths<T>,
    protected readonly resourceName: string,
    protected readonly isCachedBundle: boolean = false
  ) {
    if (!this.entityType) {
      throw new Error('Repository must be created with an entity type specified');
    }
    this.defaultFetchStrategy = isCachedBundle ? FetchStrategy.FromLocalCache : FetchStrategy.FromServer;
  }

  protected get entityType(): EntityType {
    return getEntityType(this.entityTypeLike, this.metadataStore);
  }

  protected get entityTypeName(): string {
    return this.entityType?.shortName;
  }

  protected get metadataStore(): MetadataStore {
    return this.manager.metadataStore;
  }

  protected get localResourceName(): string {
    return this.isCachedBundle ? this.entityTypeName : this.resourceName;
  }

  withId(key: any | any[], checkLocalCacheFirst?: boolean): Observable<T | null> {
    if (!this.entityTypeName) {
      throw new Error('Repository must be created with an entity type specified');
    }

    const entityKey = new EntityKey(this.entityType, key);
    if (checkLocalCacheFirst) {
      const entity = this.manager.getEntityByKey(entityKey) as T;
      if (entity) {
        return of(entity);
      }
    }

    // Make sure we have the correct default resource name
    this.setResourceName();
    const query = this.withDefaultExpand(EntityQuery.fromEntityKey(entityKey));
    return this.executeQuery(query).pipe(map((data) => data[0] || null));
  }

  fetch(queryAction?: QueryBuilderAction<T>, options?: FetchOptions): Observable<T[]>;
  fetch<TResult>(queryAction?: QueryBuilderAction<T>, options?: FetchOptions): Observable<TResult[]>;
  fetch(args: FetchParams<T>, options?: FetchOptions): Observable<EntityLike<T>[]>;
  fetch<TResult extends Entity>(args: FetchParamsToType<T, TResult>, options?: FetchOptions): Observable<TResult[]>;
  fetch<TResult, TParams>(resourceName: string, params?: TParams): Observable<TResult[]>;
  fetch<TResult, TEntityResult extends Entity, TParams>(
    args: string | QueryBuilderAction<T> | FetchParams<T> | FetchParamsToType<T, TEntityResult>,
    optionsOrParams?: TParams | FetchOptions
  ): Observable<(TResult | T | EntityLike<T> | TEntityResult)[]> {
    if (typeof args !== 'string') {
      return this.fetchQuery(args, optionsOrParams as FetchOptions);
    } else {
      return this.fetchResource<TResult, TParams>(args, optionsOrParams as TParams);
    }
  }

  fetchPage(pageSize: number, queryAction: QueryBuilderAction<T>): Paginator<T>;
  fetchPage(pageSize: number, args: FetchParams<T>): Paginator<EntityLike<T>>;
  fetchPage<TResult extends Entity>(pageSize: number, args: FetchParamsToType<T, TResult>): Paginator<TResult>;
  fetchPage<TResult extends Entity>(
    pageSize: number,
    args: QueryBuilderAction<T> | FetchParams<T> | FetchParamsToType<T, TResult>
  ): Paginator<T | EntityLike<T> | TResult> {
    const currentPage$ = new BehaviorSubject({
      pageIndex: 0,
      pageSize,
    });
    args =
      typeof args === 'function' ? { queryAction: args, select: null } : args || { queryAction: null, select: null };
    const queryBuilder = new EntityQueryBuilder(this.baseQuery());
    if (args.queryAction) {
      args.queryAction(queryBuilder);
    }

    const isSelection = !!(args.select && args.select.length);
    let query = isSelection
      ? queryBuilder.entityQuery.select(args.select as string[])
      : this.withDefaultExpand(queryBuilder.entityQuery);

    if (this.isFetchParamsToType(args)) {
      const entityType = getEntityType(args.toType, this.metadataStore, false);
      query = query.toType(entityType);
    }

    const pageResult$ = currentPage$.pipe(
      concatMap((currentPage) =>
        this.executeQuery(
          query
            .skip(currentPage.pageIndex * (currentPage.pageSize || pageSize))
            .take(currentPage.pageSize || pageSize)
            .inlineCount(),
          { observe: 'response' }
        ).pipe(
          map((queryResult) => ({
            queryResult,
            pageIndex: currentPage.pageIndex,
            totalNumberOfPages: Math.ceil(queryResult.inlineCount / (currentPage.pageSize || pageSize)),
            pageSize: (pageSize = currentPage.pageSize || pageSize),
          }))
        )
      )
    );

    return {
      page$: pageResult$.pipe(
        map((pageResult) => ({
          items:
            isSelection && !this.isFetchParamsToType(args)
              ? pageResult.queryResult.results.map((r: EntityLike<T>) => this.mapResult(r))
              : pageResult.queryResult.results,
          totalNumberOfPages: pageResult.totalNumberOfPages,
          pageIndex: pageResult.pageIndex,
          itemCount: pageResult.queryResult.inlineCount,
          pageSize: pageResult.pageSize,
        })),
        shareReplay({ bufferSize: 1, refCount: true })
      ),
      setPageIndex: (newPageIndex: number, newPageSize?: number): void => {
        currentPage$.next({
          pageIndex: newPageIndex,
          pageSize: newPageSize,
        });
      },
    };
  }

  count(queryAction: QueryBuilderAction<T>): Observable<number> {
    const queryBuilder = new EntityQueryBuilder(this.baseQuery());
    if (queryAction) {
      queryAction(queryBuilder);
    }

    return this.executeQuery(queryBuilder.entityQuery.take(0).inlineCount(), { observe: 'response' }).pipe(
      map((queryResult) => queryResult.inlineCount)
    );
  }

  protected fetchQuery<TResult extends Entity>(
    args: QueryBuilderAction<T> | FetchParams<T> | FetchParamsToType<T, TResult>,
    options?: FetchOptions
  ): Observable<(T | EntityLike<T> | TResult)[]> {
    args =
      typeof args === 'function' ? { queryAction: args, select: null } : args || { queryAction: null, select: null };
    const queryBuilder = new EntityQueryBuilder(this.baseQuery());
    if (args.queryAction) {
      args.queryAction(queryBuilder);
    }

    const isSelection = !!(args.select && args.select.length);
    let query = isSelection
      ? queryBuilder.entityQuery.select(args.select as string[])
      : this.withDefaultExpand(queryBuilder.entityQuery);

    if (this.isFetchParamsToType(args)) {
      const entityType = getEntityType(args.toType, this.metadataStore, false);
      query = query.toType(entityType);
    }

    let result: Observable<(T | EntityLike<T> | TResult)[]>;
    if (options?.refetchOnClear) {
      result = this.helper.cleared$.pipe(
        map((event) => event.manager),
        startWith(this.manager),
        filter((manager) => manager === this.manager),
        concatMap(() => this.executeQuery(query))
      );
    } else {
      result = this.executeQuery(query, { executeLocally: options?.executeLocally });
    }
    if (options?.replayLastResult) {
      result = result.pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }

    return result.pipe(map((results) => results.map((r) => this.mapResult(r))));
  }

  protected isFetchParamsToType<TResult extends Entity>(args: any): args is FetchParamsToType<T, TResult> {
    return !!(args as FetchParamsToType<T, TResult>).toType;
  }

  protected fetchResource<TResult, TParams>(resourceName: string, params?: TParams): Observable<TResult[]> {
    const qb = new EntityQueryBuilder(EntityQuery.from(resourceName));
    if (params) {
      qb.withParameters(params);
    }

    return this.executeQuery(qb.entityQuery).pipe(
      map((results) => results.map((result) => this.mapResult(result as unknown as TResult)))
    );
  }

  protected baseQuery(): EntityQuery {
    const query = EntityQuery.from(this.localResourceName);
    // Setting this will make sure the query gets built for the correct entity type
    query.fromEntityType = this.entityType;
    return query;
  }

  protected withDefaultExpand(query: EntityQuery): EntityQuery {
    // Add expandClause if query doens't have one yet
    if (!query.expandClause && this.expand && this.expand.length) {
      query = query.expand(this.expand as string[]);
    }
    return query;
  }

  protected executeQuery(
    query: EntityQuery,
    options?: {
      fetchStrategy?: FetchStrategy;
      executeLocally?: boolean;
    }
  ): Observable<T[]>;
  protected executeQuery(
    query: EntityQuery,
    options?: {
      fetchStrategy?: FetchStrategy;
      observe: 'response';
      executeLocally?: false;
    }
  ): Observable<QueryResult>;
  protected executeQuery(
    query: EntityQuery,
    options?: {
      fetchStrategy?: FetchStrategy;
      observe?: string;
      executeLocally?: boolean;
    }
  ): Observable<T[]> | Observable<QueryResult> {
    options = options || {};
    const q = query.using(options.fetchStrategy || this.defaultFetchStrategy);

    if (options.observe === 'response') {
      return defer(() => this.helper.busyService.busy$('Loading data...', from(this.manager.executeQuery(q))));
    }

    return defer(() =>
      this.helper.busyService
        .busy$(
          'Loading data...',
          options.executeLocally
            ? of(this.manager.executeQueryLocally(q))
            : from(this.manager.executeQuery(q)).pipe(map((response) => response.results))
        )
        .pipe(map((results) => results as T[]))
    );
  }

  protected executeCacheQuery(query: any): T[] {
    return this.manager.executeQueryLocally(query) as T[];
  }

  protected setResourceName(): void {
    if (this.entityType) {
      const defaultResourceName = this.localResourceName;
      if (this.entityType.defaultResourceName !== defaultResourceName) {
        this.entityType.setProperties({
          defaultResourceName,
        });
        this.metadataStore.setEntityTypeForResourceName(defaultResourceName, this.entityType);
      }
    } else {
      throw new Error(this.entityTypeName + ' does not exist! Repository must be created for an existing entity type!');
    }
  }

  protected mapResult<TResult>(result: TResult): TResult {
    // Don't touch entities
    if (isEntity(result as unknown as Entity)) {
      return result;
    }

    return mapJsonObjectToClient(result, {
      customizer: (obj, key, value) => {
        // Leave entities as-is
        if (isEntity(value)) {
          return value;
        }
        if (Array.isArray(value) && value.every((entity) => isEntity(entity))) {
          return value;
        }

        return;
      },
    });
  }
}
